/**
 * SAML Callback Handler
 * @file Handles SAML OAuth authorization code exchange with token deduplication
 * @description
 * This module manages the SAML authentication callback process by:
 * - Extracting authorization code from URL parameters
 * - Ensuring token exchange happens only once (prevents race conditions)
 * - Storing authentication tokens securely
 * - Cleaning up URL parameters after successful authentication
 * - Providing utilities for subsequent auth operations with the same lock mechanism
 */

import { Auth } from 'aws-amplify';

// ✅ Token Exchange Lock: Prevents multiple simultaneous token exchanges
let tokenExchangeInProgress = false;
let tokenExchangePromise = null;
let cachedSession = null;

/**
 * Main SAML Callback Handler
 * Processes OAuth authorization code from SAML redirect
 * Ensures token exchange happens only ONCE with proper locking
 * @async
 * @returns {Promise<Object|null>} The Amplify session object or null if no code found
 * @throws {Error} If token exchange fails
 */
export const initiateSAMLCallback = async () => {
  try {
    // Extract authorization code from URL
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code');
    const state = urlParams.get('state');

    if (!code) {
      console.log('[SAML Callback] No authorization code in URL, skipping SAML callback');
      return null;
    }

    console.log('[SAML Callback] Authorization code detected in URL', {
      hasCode: !!code,
      hasState: !!state,
      codeLength: code ? code.length : 0
    });

    // ✅ LOCK: Prevent duplicate token exchanges
    if (tokenExchangeInProgress) {
      console.log('[SAML Callback] Token exchange already in progress, returning cached promise');
      return tokenExchangePromise;
    }

    // ✅ Mark exchange as in progress
    tokenExchangeInProgress = true;

    // ✅ Create and cache the token exchange promise
    tokenExchangePromise = (async () => {
      try {
        console.log('[SAML Callback] Initiating token exchange with authorization code');

        // Exchange authorization code for tokens via Amplify
        const session = await Auth.currentSession();
        cachedSession = session;

        console.log('[SAML Callback] Token exchange successful', {
          hasIdToken: !!session.getIdToken(),
          hasAccessToken: !!session.getAccessToken(),
          hasRefreshToken: !!session.getRefreshToken(),
          timestamp: new Date().toISOString()
        });

        // ✅ CLEAN UP: Remove URL parameters to prevent re-exchange
        // Use replaceState to avoid adding history entry
        window.history.replaceState(
          {},
          document.title,
          window.location.pathname
        );

        console.log('[SAML Callback] URL parameters cleaned successfully');

        // ✅ Extract tokens from session
        const idToken = session.getIdToken().getJwtToken();
        const accessToken = session.getAccessToken().getJwtToken();
        const refreshToken = session.getRefreshToken().getToken();

        // ✅ Store tokens in localStorage for later use
        localStorage.setItem("accessToken", accessToken);
        localStorage.setItem("idToken", idToken);
        localStorage.setItem("Token", accessToken);
        localStorage.setItem("refreshToken", refreshToken);
        localStorage.setItem("authMethod", "SAML");

        console.log('[SAML Callback] Tokens stored in localStorage', {
          hasAccessToken: !!accessToken,
          hasIdToken: !!idToken,
          hasRefreshToken: !!refreshToken,
          tokenStorageTime: new Date().toISOString()
        });

        // ✅ Decode JWT to extract user information
        const tokenPayload = JSON.parse(
          atob(idToken.split('.')[1])
        );

        console.log('[SAML Callback] JWT decoded successfully', {
          email: tokenPayload.email,
          sub: tokenPayload.sub,
          iss: tokenPayload.iss,
          aud: tokenPayload.aud,
          exp: new Date(tokenPayload.exp * 1000).toISOString()
        });

        // ✅ Create and store user object
        const userObject = {
          userEmail: tokenPayload.email,
          userSub: tokenPayload.sub,
          data: {
            idToken: idToken,
            accessToken: accessToken,
            refreshToken: refreshToken,
          },
          authMethod: "SAML",
          loginTime: new Date().toISOString(),
          tokenExpiry: new Date(tokenPayload.exp * 1000).toISOString()
        };

        localStorage.setItem('user', JSON.stringify(userObject));

        console.log('[SAML Callback] User object stored in localStorage', {
          email: userObject.userEmail,
          authMethod: userObject.authMethod,
          loginTime: userObject.loginTime
        });

        // Upload SAML success logs (async, non-blocking)
        try {
          // This will be called from login component if available
          if (window.uploadSAMLLogs) {
            window.uploadSAMLLogs('SAML_CALLBACK_SUCCESS_Log');
          }
        } catch (logError) {
          console.warn('[SAML Callback] Failed to upload logs:', logError);
        }

        return session;

      } catch (error) {
        console.error('[SAML Callback] Token exchange failed during callback', {
          error: error.message,
          stack: error.stack,
          timestamp: new Date().toISOString()
        });

        // Clear locks on error
        tokenExchangeInProgress = false;
        tokenExchangePromise = null;
        cachedSession = null;

        // Upload error logs
        try {
          if (window.uploadSAMLLogs) {
            window.uploadSAMLLogs('SAML_CALLBACK_ERROR_Log');
          }
        } catch (logError) {
          console.warn('[SAML Callback] Failed to upload error logs:', logError);
        }

        throw error;
      } finally {
        // Mark exchange as complete (but keep cached session)
        tokenExchangeInProgress = false;
      }
    })();

    return tokenExchangePromise;

  } catch (error) {
    console.error('[SAML Callback] Unexpected error in initiateSAMLCallback:', error);
    tokenExchangeInProgress = false;
    tokenExchangePromise = null;
    throw error;
  }
};

/**
 * Get Session with Token Exchange Lock
 * Ensures only one token exchange happens, with others waiting for the same result
 * @async
 * @returns {Promise<Object>} The Amplify session object
 * @throws {Error} If no session available and exchange fails
 */
export const getSessionWithLock = async () => {
  try {
    // ✅ If exchange is in progress, return the pending promise
    if (tokenExchangeInProgress && tokenExchangePromise) {
      console.log('[Auth Lock] Token exchange in progress, waiting for result');
      return tokenExchangePromise;
    }

    // ✅ If we have a cached session, return it
    if (cachedSession) {
      console.log('[Auth Lock] Returning cached session');
      try {
        // Verify cached session is still valid
        await cachedSession.getIdToken().getJwtToken();
        return cachedSession;
      } catch (e) {
        console.log('[Auth Lock] Cached session expired, refreshing');
        cachedSession = null;
      }
    }

    // ✅ Get fresh session without triggering new exchange
    console.log('[Auth Lock] Getting fresh session');
    const session = await Auth.currentSession();
    cachedSession = session;
    return session;

  } catch (error) {
    console.error('[Auth Lock] Failed to get session:', error);
    cachedSession = null;
    throw error;
  }
};

/**
 * Refresh Session with Lock
 * Handles token refresh while maintaining the lock mechanism
 * @async
 * @returns {Promise<Object>} The new Amplify session object
 * @throws {Error} If token refresh fails
 */
export const refreshSessionWithLock = async () => {
  try {
    console.log('[Auth Refresh] Initiating token refresh');

    // ✅ Clear cached session to force refresh
    cachedSession = null;

    // ✅ Get fresh tokens
    const session = await Auth.currentSession();
    cachedSession = session;

    // ✅ Update localStorage with new tokens
    const idToken = session.getIdToken().getJwtToken();
    const accessToken = session.getAccessToken().getJwtToken();
    const refreshToken = session.getRefreshToken().getToken();

    localStorage.setItem("accessToken", accessToken);
    localStorage.setItem("idToken", idToken);
    localStorage.setItem("Token", accessToken);
    localStorage.setItem("refreshToken", refreshToken);

    console.log('[Auth Refresh] Tokens refreshed successfully', {
      timestamp: new Date().toISOString()
    });

    return session;

  } catch (error) {
    console.error('[Auth Refresh] Token refresh failed:', error);
    cachedSession = null;
    throw error;
  }
};

/**
 * Clear SAML Cache
 * Clears cached session and resets locks
 * Used during logout or when authentication fails
 * @returns {void}
 */
export const clearSAMLCache = () => {
  console.log('[SAML Cache] Clearing cache and locks');
  tokenExchangeInProgress = false;
  tokenExchangePromise = null;
  cachedSession = null;
  console.log('[SAML Cache] Cache cleared successfully');
};

/**
 * Check if SAML Authentication is Valid
 * @returns {boolean} True if SAML auth tokens are present and valid
 */
export const isSAMLAuthValid = () => {
  try {
    const authMethod = localStorage.getItem("authMethod");
    const user = localStorage.getItem("user");
    const token = localStorage.getItem("Token");

    if (authMethod !== "SAML" || !user || !token) {
      return false;
    }

    // Try to parse user object
    const userObj = JSON.parse(user);
    return !!userObj.userEmail;

  } catch (error) {
    console.error('[SAML Auth Check] Error checking SAML auth validity:', error);
    return false;
  }
};

/**
 * Get SAML User Email
 * @returns {string|null} User email from SAML token or null if not available
 */
export const getSAMLUserEmail = () => {
  try {
    const user = localStorage.getItem("user");
    if (!user) return null;

    const userObj = JSON.parse(user);
    return userObj.userEmail || null;

  } catch (error) {
    console.error('[SAML Utils] Error getting user email:', error);
    return null;
  }
};

/**
 * Get Token Expiry Time
 * @returns {Date|null} Token expiry date or null if not available
 */
export const getTokenExpiryTime = () => {
  try {
    const token = localStorage.getItem("Token");
    if (!token) return null;

    // Decode JWT
    const payload = JSON.parse(atob(token.split('.')[1]));
    return new Date(payload.exp * 1000);

  } catch (error) {
    console.error('[SAML Utils] Error getting token expiry:', error);
    return null;
  }
};

/**
 * Check if Token is Expired
 * @returns {boolean} True if token is expired
 */
export const isTokenExpired = () => {
  try {
    const expiryTime = getTokenExpiryTime();
    if (!expiryTime) return true;

    const now = new Date();
    // Consider expired if less than 5 minutes remaining
    const bufferMs = 5 * 60 * 1000;
    return now.getTime() >= (expiryTime.getTime() - bufferMs);

  } catch (error) {
    console.error('[SAML Utils] Error checking token expiry:', error);
    return true;
  }
};

/**
 * Handle SAML Logout
 * Clears all authentication data and caches
 * @returns {void}
 */
export const handleSAMLLogout = () => {
  try {
    console.log('[SAML Logout] Initiating SAML logout');

    // Clear locks and cache
    clearSAMLCache();

    // Clear localStorage
    localStorage.removeItem("accessToken");
    localStorage.removeItem("idToken");
    localStorage.removeItem("Token");
    localStorage.removeItem("refreshToken");
    localStorage.removeItem("user");
    localStorage.removeItem("authMethod");
    localStorage.removeItem("controlUser");
    localStorage.removeItem("controlPermissions");
    localStorage.removeItem("controlAccessToken");

    console.log('[SAML Logout] All authentication data cleared');

  } catch (error) {
    console.error('[SAML Logout] Error during logout:', error);
  }
};

export default {
  initiateSAMLCallback,
  getSessionWithLock,
  refreshSessionWithLock,
  clearSAMLCache,
  isSAMLAuthValid,
  getSAMLUserEmail,
  getTokenExpiryTime,
  isTokenExpired,
  handleSAMLLogout
};
